// ─── ChartPopup ───────────────────────────────────────────────────────────────
// Modal that graphs the Visualize table data. Driven by the chart-type icon
// selected in the toolbar. Multiple selected parameters render as multiple
// traces on one chart. Reuses the already-fetched `series` from useVisualizeData.
//
// Header carries a 3-dot menu (matching the XD): Chart Settings, Generate Report,
// Create Widget, Export, Delete. "Chart Settings" opens a tabbed modal (Y-Axis,
// Titles, Colors, Data) whose values re-render the chart on Save.
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useRef } from 'react'
import { X, MoreVertical } from 'lucide-react'
import { dashboardApi } from '../../../lib/dashboardApi'
import { useDashboardStore } from '../../../store/dashboard-store'
import {
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Label,
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  RadialBarChart, RadialBar,
} from 'recharts'

const COLORS = ['#358ECD', '#E36B6B', '#5EA64D', '#E0A030', '#8E5EA6', '#3FA7A0', '#C0567B', '#7A8B99']

const pad = (n) => String(n).padStart(2, '0')
const fmt = (ms) => {
  const d = new Date(ms)
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const DEFAULT_SETTINGS = {
  yMin: '', yMax: '', ySteps: '',
  chartTitle: '', xTitle: '', yTitle: '',
  colors: {},
  smoothLine: false, smoothMethod: 'moving-average', avgWindow: 5,
  removeSpikes: false, stdDev: 2,
}

// Persist chart settings across refreshes (localStorage — no backend/DB needed).
const SETTINGS_KEY = 'wqdata_da_chart_settings_v1'
function loadSettings() {
  try {
    const s = JSON.parse(localStorage.getItem(SETTINGS_KEY))
    return s ? { ...DEFAULT_SETTINGS, ...s } : DEFAULT_SETTINGS
  } catch { return DEFAULT_SETTINGS }
}
function saveSettings(s) {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)) } catch { /* ignore quota */ }
}

// ── data transforms driven by Chart Settings → Data tab ──
function mean(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0 }
function stdDev(arr, m) { return arr.length ? Math.sqrt(mean(arr.map((v) => (v - m) ** 2))) : 0 }

function applyDataSettings(series, s) {
  return series.map((ser) => {
    let pts = ser.points
    if (s.removeSpikes && pts.length > 2) {
      const ys = pts.map((p) => p.y)
      const m = mean(ys), sd = stdDev(ys, m), k = Number(s.stdDev) || 2
      if (sd > 0) pts = pts.filter((p) => Math.abs(p.y - m) <= k * sd)
    }
    if (s.smoothLine) {
      const w = Math.max(1, Number(s.avgWindow) || 1)
      pts = pts.map((p, i) => {
        const lo = Math.max(0, i - Math.floor(w / 2))
        const win = pts.slice(lo, lo + w)
        return { x: p.x, y: mean(win.map((q) => q.y)) }
      })
    }
    return { ...ser, points: pts }
  })
}

function toRows(series) {
  const map = new Map()
  series.forEach((s) => {
    s.points.forEach((pt) => {
      const row = map.get(pt.x) || { x: pt.x, label: fmt(pt.x) }
      row[`p${s.id}`] = pt.y
      map.set(pt.x, row)
    })
  })
  return [...map.values()].sort((a, b) => a.x - b.x)
}

export default function ChartPopup({ open, onClose, chartType, series = [] }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settings, setSettings] = useState(loadSettings)
  const [busy, setBusy] = useState('')
  const chartRef = useRef(null)
  const activeDashboard = useDashboardStore((s) => s.activeDashboard)

  if (!open) return null

  const colorFor = (s, i) => settings.colors[s.id] || COLORS[i % COLORS.length]
  const effSeries = applyDataSettings(series, settings)
  const hasData = effSeries.some((s) => s.points.length > 0)
  const rows = toRows(effSeries)

  const isPie = ['pie', 'donut', 'half-pie'].includes(chartType)
  const isArea = chartType === 'area'
  const isBar = ['bar', 'column', 'column-grp', 'h-bar'].includes(chartType)
  const isRadar = chartType === 'radar'
  const isGauge = chartType === 'gauge'
  const horizontal = chartType === 'h-bar'

  const yDomain = [
    settings.yMin !== '' ? Number(settings.yMin) : 'auto',
    settings.yMax !== '' ? Number(settings.yMax) : 'auto',
  ]
  // Explicit ticks so the configured min/max (and steps) actually render on the
  // axis — Recharts otherwise auto-rounds to "nice" values and hides them.
  const yTicks = (() => {
    if (settings.yMin === '' || settings.yMax === '') return undefined
    const mn = Number(settings.yMin), mx = Number(settings.yMax)
    if (!(mx > mn)) return undefined
    const steps = settings.ySteps !== '' ? Math.max(1, Math.round(Number(settings.ySteps))) : 4
    return Array.from({ length: steps + 1 }, (_, i) => Math.round((mn + (mx - mn) * i / steps) * 1000) / 1000)
  })()
  const lineType = settings.smoothLine ? 'monotone' : 'linear'

  const pieData = effSeries.map((s, i) => ({
    name: s.unit ? `${s.name} (${s.unit})` : s.name,
    value: s.points.length ? mean(s.points.map((p) => p.y)) : 0,
    fill: colorFor(s, i),
  }))
  const radarRows = (() => {
    if (!rows.length) return []
    const step = Math.max(1, Math.ceil(rows.length / 12))
    return rows.filter((_, i) => i % step === 0)
  })()

  const yAxis = (extra = {}) => (
    <YAxis tick={{ fontSize: 11 }} domain={yDomain} allowDataOverflow={true} ticks={yTicks} {...extra}>
      {settings.yTitle && <Label value={settings.yTitle} angle={-90} position="insideLeft" style={{ fontSize: 11 }} />}
    </YAxis>
  )
  const xAxis = (props = {}) => (
    <XAxis dataKey="label" tick={{ fontSize: 11 }} {...props}>
      {settings.xTitle && <Label value={settings.xTitle} position="insideBottom" offset={-2} style={{ fontSize: 11 }} />}
    </XAxis>
  )

  const renderChart = () => {
    if (!hasData) return <div className="flex items-center justify-center h-full text-sm text-gray-400">No data to plot for the selected range.</div>
    if (isPie) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy={chartType === 'half-pie' ? '75%' : '50%'}
              startAngle={chartType === 'half-pie' ? 180 : 0} endAngle={chartType === 'half-pie' ? 0 : 360}
              innerRadius={chartType === 'donut' ? 60 : 0} outerRadius={120} label>
              {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip /><Legend />
          </PieChart>
        </ResponsiveContainer>
      )
    }
    if (isArea) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows}><CartesianGrid strokeDasharray="3 3" />{xAxis()}{yAxis()}<Tooltip /><Legend />
            {effSeries.map((s, i) => <Area key={s.id} type={lineType} dataKey={`p${s.id}`} name={s.name} stroke={colorFor(s, i)} fill={colorFor(s, i)} fillOpacity={0.25} connectNulls />)}
          </AreaChart>
        </ResponsiveContainer>
      )
    }
    if (isBar) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout={horizontal ? 'vertical' : 'horizontal'}><CartesianGrid strokeDasharray="3 3" />
            {horizontal
              ? (<><XAxis type="number" tick={{ fontSize: 11 }} domain={yDomain} allowDataOverflow={true} ticks={yTicks} /><YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={90} /></>)
              : (<>{xAxis()}{yAxis()}</>)}
            <Tooltip /><Legend />
            {effSeries.map((s, i) => <Bar key={s.id} dataKey={`p${s.id}`} name={s.name} fill={colorFor(s, i)} />)}
          </BarChart>
        </ResponsiveContainer>
      )
    }
    if (isRadar) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarRows}><PolarGrid /><PolarAngleAxis dataKey="label" tick={{ fontSize: 10 }} /><PolarRadiusAxis tick={{ fontSize: 10 }} /><Tooltip /><Legend />
            {effSeries.map((s, i) => <Radar key={s.id} name={s.name} dataKey={`p${s.id}`} stroke={colorFor(s, i)} fill={colorFor(s, i)} fillOpacity={0.25} />)}
          </RadarChart>
        </ResponsiveContainer>
      )
    }
    if (isGauge) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart data={pieData} innerRadius="30%" outerRadius="100%" startAngle={180} endAngle={0}><RadialBar background dataKey="value" /><Tooltip /><Legend /></RadialBarChart>
        </ResponsiveContainer>
      )
    }
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows}><CartesianGrid strokeDasharray="3 3" />{xAxis()}{yAxis()}<Tooltip /><Legend />
          {effSeries.map((s, i) => <Line key={s.id} type={lineType} dataKey={`p${s.id}`} name={s.name} stroke={colorFor(s, i)} dot={false} connectNulls />)}
        </LineChart>
      </ResponsiveContainer>
    )
  }

  const exportPng = async () => {
    setMenuOpen(false)
    const { default: html2canvas } = await import('html2canvas')
    if (!chartRef.current) return
    const canvas = await html2canvas(chartRef.current, { scale: 2, backgroundColor: '#fff' })
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `${(settings.chartTitle || chartType) || 'chart'}.png`
    a.click()
  }

  // Generate Report → a downloadable PDF (chart image + title).
  const generateReport = async () => {
    setMenuOpen(false); setBusy('report')
    try {
      const { default: html2canvas } = await import('html2canvas')
      const { jsPDF } = await import('jspdf')
      const canvas = await html2canvas(chartRef.current, { scale: 2, backgroundColor: '#fff' })
      const img = canvas.toDataURL('image/png')
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight()
      pdf.setFontSize(14); pdf.text(settings.chartTitle || 'Data Analytics Report', 40, 40)
      const iw = pw - 80, ih = canvas.height * (iw / canvas.width)
      pdf.addImage(img, 'PNG', 40, 60, iw, Math.min(ih, ph - 100))
      pdf.save(`${settings.chartTitle || chartType || 'report'}.pdf`)
    } catch (e) { alert('Could not generate report: ' + e.message) }
    finally { setBusy('') }
  }

  // Create Widget → adds a chart widget to the active dashboard (graceful if none).
  const createWidget = async () => {
    setMenuOpen(false)
    if (!activeDashboard?.id) {
      alert('Open a dashboard first — then "Create Widget" will add this chart to it.')
      return
    }
    setBusy('widget')
    try {
      await dashboardApi.createWidget({
        dashboard: activeDashboard.id,
        widget_type: 'chart',
        title: settings.chartTitle || series.map((s) => s.name).join(', ') || 'Chart',
        data_config: { chartType, parameters: series.map((s) => s.id), settings },
      })
      alert('Widget created on dashboard: ' + (activeDashboard.name || activeDashboard.id))
    } catch (e) {
      alert('Could not create widget: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message))
    } finally { setBusy('') }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl h-[72vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header: title + 3-dot menu + close */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 relative">
          <h2 className="text-sm font-bold text-gray-800">{settings.chartTitle || `Title`}</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} title="Options" className="text-gray-500 hover:text-gray-800 p-1 rounded hover:bg-gray-100">
                <MoreVertical className="w-5 h-5" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 py-1 text-sm">
                  <button onClick={() => { setMenuOpen(false); setSettingsOpen(true) }} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">Chart Settings</button>
                  <div className="border-t border-gray-100 my-1" />
                  <button onClick={generateReport} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">Generate Report</button>
                  <button onClick={createWidget} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">Create Widget</button>
                  <button onClick={exportPng} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">Export</button>
                  <div className="border-t border-gray-100 my-1" />
                  <button disabled className="w-full text-left px-4 py-2 text-gray-400 cursor-not-allowed flex items-center gap-2">Delete</button>
                </div>
              )}
            </div>
            <button onClick={onClose} title="Close" aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div ref={chartRef} className="flex-1 p-4 min-h-0 bg-white">{renderChart()}</div>
      </div>

      {settingsOpen && (
        <ChartSettingsModal
          series={series}
          colorFor={colorFor}
          initial={settings}
          onCancel={() => setSettingsOpen(false)}
          onSave={(next) => { setSettings(next); saveSettings(next); setSettingsOpen(false) }}
        />
      )}
    </div>
  )
}

// Module-scope so they are NOT recreated each render (recreating a component
// inside render remounts its inputs and steals focus after one keystroke).
const SETTINGS_TABS = ['Y-Axis', 'Titles', 'Colors', 'Data']
const SETTINGS_INPUT = 'w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#358ECD]'
const Field = ({ label, children }) => (
  <div className="mb-4"><label className="block text-sm text-gray-700 mb-1">{label}</label>{children}</div>
)

// ── Chart Settings tabbed modal (Y-Axis / Titles / Colors / Data) ──
function ChartSettingsModal({ series, colorFor, initial, onCancel, onSave }) {
  const [tab, setTab] = useState('Y-Axis')
  const [draft, setDraft] = useState(initial)
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))
  const TABS = SETTINGS_TABS
  const inputCls = SETTINGS_INPUT

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1100] p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-bold text-gray-800">Chart Settings</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => onSave(draft)} className="px-3 py-1 bg-[#358ECD] text-white text-sm rounded hover:bg-[#2d7ab5]">Save</button>
            <button onClick={onCancel} className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50">Cancel</button>
          </div>
        </div>
        <div className="flex gap-6 px-5 pt-3 border-b border-gray-200">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`pb-2 text-sm border-b-2 transition ${tab === t ? 'border-[#358ECD] text-[#358ECD]' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>{t}</button>
          ))}
        </div>

        <div className="px-5 py-4 min-h-[220px]">
          {tab === 'Y-Axis' && (<>
            <Field label="Minimum Value"><input className={inputCls} placeholder="Default" value={draft.yMin} onChange={(e) => set({ yMin: e.target.value })} /></Field>
            <Field label="Maximum Value"><input className={inputCls} placeholder="Default" value={draft.yMax} onChange={(e) => set({ yMax: e.target.value })} /></Field>
            <Field label="Steps"><input className={inputCls} placeholder="Default" value={draft.ySteps} onChange={(e) => set({ ySteps: e.target.value })} /></Field>
          </>)}
          {tab === 'Titles' && (<>
            <Field label="Chart Title"><input className={inputCls} placeholder="Default" value={draft.chartTitle} onChange={(e) => set({ chartTitle: e.target.value })} /></Field>
            <Field label="X-Axis Title"><input className={inputCls} placeholder="Default" value={draft.xTitle} onChange={(e) => set({ xTitle: e.target.value })} /></Field>
            <Field label="Y-Axis Title"><input className={inputCls} placeholder="Default" value={draft.yTitle} onChange={(e) => set({ yTitle: e.target.value })} /></Field>
          </>)}
          {tab === 'Colors' && (
            <div className="space-y-3">
              {series.map((s, i) => (
                <div key={s.id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{[s.siteName, s.deviceName, s.unit ? `${s.name} (${s.unit})` : s.name].filter(Boolean).join(' | ')}</span>
                  <input type="color" value={draft.colors[s.id] || colorFor(s, i)} onChange={(e) => set({ colors: { ...draft.colors, [s.id]: e.target.value } })} className="w-10 h-7 border border-gray-300 rounded cursor-pointer" />
                </div>
              ))}
              {!series.length && <p className="text-sm text-gray-400">No parameters selected.</p>}
            </div>
          )}
          {tab === 'Data' && (<>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={draft.smoothLine} onChange={(e) => set({ smoothLine: e.target.checked })} /> Smooth Line
            </label>
            <div className={`pl-6 mb-4 ${draft.smoothLine ? '' : 'opacity-40 pointer-events-none'}`}>
              <select className={inputCls + ' mb-2'} value={draft.smoothMethod} onChange={(e) => set({ smoothMethod: e.target.value })}>
                <option value="moving-average">Moving Average</option>
              </select>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">Averaging Window</span>
                <input type="number" min="1" className={inputCls} placeholder="Number of Points to Average" value={draft.avgWindow} onChange={(e) => set({ avgWindow: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={draft.removeSpikes} onChange={(e) => set({ removeSpikes: e.target.checked })} /> Remove Spikes
            </label>
            <div className={`pl-6 ${draft.removeSpikes ? '' : 'opacity-40 pointer-events-none'}`}>
              <select className={inputCls + ' mb-2'} value="std-dev" readOnly><option value="std-dev">Standard Deviation</option></select>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">Increment Deviations</span>
                <input type="number" min="0" step="0.5" className={inputCls} placeholder="Number of Standard Deviations" value={draft.stdDev} onChange={(e) => set({ stdDev: e.target.value })} />
              </div>
            </div>
          </>)}
        </div>
      </div>
    </div>
  )
}
